import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Zap, TrendingUp, CheckCircle, Server, Activity, AlertTriangle } from 'lucide-react';

const PammPool = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const brokerLink = "https://hmarkets.com/live-account-pre-registration/?refid=6621&cmp=9v9g3m2r&ent=hm";

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 text-white">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto text-center mb-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/20 to-transparent blur-3xl -z-10 rounded-full" />
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 font-medium mb-6 text-sm">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
            </span>
            Live Broker Infrastructure
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
            Hands-Off Wealth Building<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
              On Auto-Pilot.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Designed for passive investors who want zero software setup, no VPS management, and pure hands-off exposure to our institutional-grade algorithms.
          </p>
          
          <a 
            href={brokerLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white rounded-xl font-bold text-lg shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all hover:scale-105"
          >
            Create PAMM Account <ArrowRight className="w-5 h-5" />
          </a>
          <p className="mt-4 text-sm text-gray-400">Minimum Capital: $500</p>
        </motion.div>
      </div>

      {/* Feature Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
        {[
          {
            icon: <Zap className="w-8 h-8 text-yellow-400" />,
            title: "Zero Setup Required",
            desc: "No software installation, no VPS configuration, and no complicated charts to watch. Deposit funds and watch them grow."
          },
          {
            icon: <Activity className="w-8 h-8 text-green-400" />,
            title: "Performance Fee Model",
            desc: "We only win when you win. A 20% performance fee is automatically deducted from net profits via High-Water Mark."
          },
          {
            icon: <Shield className="w-8 h-8 text-blue-400" />,
            title: "100% Capital Control",
            desc: "Your funds sit safely in your own broker account. We only have permission to trade, never to withdraw."
          },
          {
            icon: <AlertTriangle className="w-8 h-8 text-red-400" />,
            title: "10% Risk Limit",
            desc: "If our system ever breaches a 10% drawdown on your account, our contract is instantly cancelled. You take this defined risk, not us."
          }
        ].map((feat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.6 }}
            className="bg-gray-900/40 backdrop-blur-md border border-gray-800 p-8 rounded-2xl hover:border-blue-500/30 transition-colors"
          >
            <div className="bg-gray-800/50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 border border-gray-700">
              {feat.icon}
            </div>
            <h3 className="text-xl font-bold mb-3">{feat.title}</h3>
            <p className="text-gray-400 leading-relaxed">{feat.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Algorithms Deployed */}
      <div className="max-w-5xl mx-auto mb-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="bg-gray-900/40 backdrop-blur-md border border-gray-800 p-8 md:p-12 rounded-3xl"
        >
          <div className="flex flex-col md:flex-row gap-10 items-center">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 font-medium mb-4 text-sm">
                <Activity className="w-4 h-4" /> Flagship Algorithm
              </div>
              <h2 className="text-3xl font-bold mb-4">Predictive Machine Learning System</h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Our Tier 1 PAMM Pool is strictly powered by our flagship <strong className="text-white">Predictive Machine Learning System</strong>, deployed exclusively on the <strong className="text-white">Nasdaq 100 (US100)</strong>.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Built on a highly optimized <strong className="text-white">8-feature ONNX Machine Learning Pipeline</strong>, the system constantly evaluates live market volatility, momentum shifts, and session data. It is fortified with a strict <strong className="text-white">Drawdown-Reduction (DD-Reduction) protocol</strong> and rigorous time-series cross-validation to maintain a statistical edge.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                Utilizing advanced <strong className="text-white">Chandelier AI</strong> to dynamically navigate high-probability setups, this system boasts a verified <strong className="text-green-400">60.8% Win Rate</strong>—making it our most robust automated execution model to date.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-black/40 rounded-xl p-4 border border-gray-800">
                  <div className="text-sm text-gray-500 mb-1">Exclusive Asset</div>
                  <div className="font-bold text-white text-lg flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-400" /> US100 (Nasdaq)</div>
                </div>
                <div className="bg-black/40 rounded-xl p-4 border border-gray-800">
                  <div className="text-sm text-gray-500 mb-1">Core Technology</div>
                  <div className="font-bold text-white text-lg flex items-center gap-2"><CheckCircle className="w-4 h-4 text-cyan-400" /> Chandelier AI</div>
                </div>
                <div className="bg-black/40 rounded-xl p-4 border border-gray-800">
                  <div className="text-sm text-gray-500 mb-1">Historical Win Rate</div>
                  <div className="font-bold text-green-400 text-lg flex items-center gap-2"><TrendingUp className="w-4 h-4" /> 60.8%</div>
                </div>
                <div className="bg-black/40 rounded-xl p-4 border border-gray-800">
                  <div className="text-sm text-gray-500 mb-1">Execution Model</div>
                  <div className="font-bold text-white text-lg flex items-center gap-2"><Zap className="w-4 h-4 text-cyan-400" /> 100% Automated</div>
                </div>
              </div>
            </div>
            
            <div className="md:w-1/3 w-full">
              <div className="aspect-square bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl flex flex-col items-center justify-center p-8 text-center relative overflow-hidden shadow-2xl">
                 <div className="absolute inset-0 bg-cyan-500/5 blur-3xl" />
                 <Server className="w-16 h-16 text-cyan-400 mb-4 relative z-10" />
                 <h4 className="text-xl font-bold text-white relative z-10">Master EA Node</h4>
                 <div className="mt-4 inline-block px-3 py-1 bg-green-500/20 border border-green-500/50 rounded-full text-green-400 text-xs font-bold relative z-10">
                   ONLINE & TRADING
                 </div>
                 <p className="text-sm text-cyan-200/70 mt-4 relative z-10">Processing US100 via Chandelier AI</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Architecture Diagram */}
      <div className="max-w-5xl mx-auto mb-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-slate-900 to-gray-900 border border-gray-800 rounded-3xl p-8 md:p-12 relative overflow-hidden"
        >
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl" />
          
          <div className="text-center mb-12 relative z-10">
            <h2 className="text-3xl font-bold mb-4">Direct Broker Architecture</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Our Tier 1 PAMM Pool connects directly to our Master EA for ultra-low latency execution without the need for signal bridges or client-side VPS instances.
            </p>
          </div>

          <div className="flex flex-col items-center relative z-10">
            {/* Master Node */}
            <div className="bg-blue-900/30 border border-blue-500/50 rounded-xl p-6 text-center shadow-[0_0_30px_rgba(59,130,246,0.15)] w-full max-w-sm mb-8">
              <Server className="w-8 h-8 text-blue-400 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-white">Central Master EA</h3>
              <p className="text-sm text-blue-200/70">Ultra-Low Latency Server</p>
            </div>

            {/* Connecting Line */}
            <div className="h-16 w-px bg-gradient-to-b from-blue-500/50 to-cyan-500/50 mb-8 relative">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <ArrowRight className="w-5 h-5 text-cyan-400 rotate-90" />
              </div>
            </div>

            {/* PAMM Node */}
            <div className="bg-cyan-900/30 border border-cyan-500/50 rounded-xl p-6 text-center w-full max-w-2xl">
              <h3 className="text-xl font-bold text-cyan-400 mb-2">TIER 1: PAMM POOL</h3>
              <p className="text-gray-300 font-medium mb-4">Direct Broker Allocation</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
                <div className="bg-black/40 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm">Passive Investors</h4>
                    <p className="text-xs text-gray-400 mt-1">Zero latency execution</p>
                  </div>
                </div>
                <div className="bg-black/40 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm">Auto-Fee Splitting</h4>
                    <p className="text-xs text-gray-400 mt-1">Managed automatically by broker</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Pricing / Setup Card */}
      <div className="max-w-3xl mx-auto">
        <div className="glass-card glass-card-large p-8 md:p-12 text-center border-t-4 border-t-blue-500">
          <h2 className="text-3xl font-bold mb-6">Transparent Pricing</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-10 text-left">
            <div className="bg-gray-900/60 rounded-xl p-6 border border-gray-800">
              <div className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-2">Setup & VPS Fee</div>
              <div className="text-4xl font-bold text-white">$0</div>
              <p className="text-sm text-gray-500 mt-2">No upfront costs or monthly subscriptions.</p>
            </div>
            
            <div className="bg-gray-900/60 rounded-xl p-6 border border-gray-800">
              <div className="text-gray-400 text-sm font-medium uppercase tracking-wider mb-2">Performance Fee</div>
              <div className="text-4xl font-bold text-blue-400">20%</div>
              <p className="text-sm text-gray-500 mt-2">Deducted from net profits via High-Water Mark.</p>
            </div>
          </div>

          <a 
            href={brokerLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block w-full sm:w-auto px-12 py-4 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-colors"
          >
            Start Investing Now
          </a>
          <p className="mt-4 text-xs text-gray-500 max-w-lg mx-auto">
            By clicking above, you will be redirected to Hantec Markets to pre-register your live PAMM account under our IB link.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PammPool;
